import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Input from '~/components/Input'

const InputPage: Component = () => {
  return (
    <>
      <Title>Input - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Input</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            El componente de entrada fundamental. Input combina una estética premium con funcionalidades avanzadas como validación inline, soporte para iconos y estados interactivos refinados.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes de Diseño */}
          <Card>
            <Card.Header
              title="Estilos de Superficie"
              subtitle="Tres variantes arquitectónicas para diferentes densidades de información y estilos visuales."
              icon={<Icon icon="material-symbols-light:masks-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-8 p-10 bg-surface-muted rounded-2xl border border-border">
                <div class="space-y-4">
                  <Badge color="info" variant="soft" size="sm">
                    DEFAULT (FILLED)
                  </Badge>
                  <Input variant="default" label="Nombre de Usuario" placeholder="juan_dev" />
                </div>
                <div class="space-y-4">
                  <Badge color="secondary" variant="soft" size="sm">
                    OUTLINED
                  </Badge>
                  <Input variant="outlined" label="Correo Corporativo" placeholder="juan@florezjaramillo.com" />
                </div>
                <div class="space-y-4">
                  <Badge color="warning" variant="soft" size="sm">
                    TEXT (UNDERLINED)
                  </Badge>
                  <Input variant="text" label="Puesto de Trabajo" placeholder="Senior Developer" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Adornos y Herramientas */}
          <Card>
            <Card.Header
              title="Iconografía y Adornos"
              subtitle="Enriquece la experiencia de usuario inyectando glifos visuales o botones de acción dentro del campo."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Decoradores de Entrada</h4>
                  <div class="space-y-6">
                    <Input label="Búsqueda Global" startIcon="material-symbols-light:search" placeholder="Comienza a escribir..." />
                    <Input label="Precio Estimado" startIcon="material-symbols-light:attach-money" endIcon="material-symbols-light:list" placeholder="0.00" />
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-secondary-500">Acciones Integradas</h4>
                  <div class="space-y-6">
                    <Input label="Token de Acceso" type="password" placeholder="••••••••" />
                    <Input
                      label="Enlace de Invitación"
                      placeholder="https://app.com/invite/..."
                      appendIcon={
                        <Button variant="contained" size="sm" class="rounded-lg">
                          Copiar
                        </Button>
                      }
                    />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Estados Críticos */}
          <Card>
            <Card.Header
              title="Estados de Validación"
              subtitle="Comunicación visual clara mediante cambios de color y mensajes de soporte para errores o guías."
              icon={<Icon icon="material-symbols-light:warning-outline" class="text-red-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 bg-surface rounded-2xl border border-border shadow-sm grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="space-y-2">
                  <Input label="Campo Obligatorio" error="Este dato es vital para continuar" placeholder="Valor necesario" />
                </div>
                <div class="space-y-2">
                  <Input label="Información Sensible" helperText="Usamos cifrado de grado militar para este dato." placeholder="Escribe aquí" />
                </div>
                <div class="space-y-2 opacity-60">
                  <Input label="Cuenta Congelada" disabled value="juan_perez@locked.com" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Layouts Avanzados — NEW SECTION */}
          <Card>
            <Card.Header
              title="Escenarios de Uso del Mundo Real"
              subtitle="Combinaciones de inputs para flujos de autenticación, pagos y recolección de datos masiva."
              icon={<Icon icon="material-symbols-light:language" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="p-8 bg-surface-muted rounded-xl border border-dashed border-border space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle">FLUJO DE PAGO</h4>
                  <Input label="Titular de la Tarjeta" placeholder="NOMBRE COMPLETO" startIcon="material-symbols-light:person-outline" />
                  <div class="grid grid-cols-2 gap-4">
                    <Input label="Expira" placeholder="MM/YY" />
                    <Input label="CVC" placeholder="•••" type="password" />
                  </div>
                </div>
                <div class="p-8 bg-surface-muted rounded-xl border border-dashed border-border space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle">CONFIGURACIÓN DE CUENTA</h4>
                  <Input
                    label="URL Personalizada"
                    startIcon="material-symbols-light:link"
                    placeholder="username"
                    appendIcon={
                      <Badge color="primary" variant="soft">
                        .fj.com
                      </Badge>
                    }
                  />
                  <Input label="Teléfono Móvil" startIcon="material-symbols-light:call-outline" placeholder="+57 321 000 0000" endIcon="material-symbols-light:verified-outline" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <Input size="xs" label="Extra Small" placeholder="xs" />
                <Input size="sm" label="Small" placeholder="sm" />
                <Input size="md" label="Medium" placeholder="md" />
                <Input size="lg" label="Large" placeholder="lg" />
                <Input size="xl" label="Extra Large" placeholder="xl" />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Input."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">label</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Etiqueta flotante del campo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">variant</td>
                      <td class="px-6 py-4 italic text-xs">'default' | 'outlined' | 'text'</td>
                      <td class="px-6 py-4 font-mono">'default'</td>
                      <td class="px-6 py-4 italic">Estetica visual de la superficie.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Altura y tipografía del campo (36/40/48/56/64px).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">type</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">'text'</td>
                      <td class="px-6 py-4">Tipo nativo del input (email, password, etc).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">startIcon / endIcon</td>
                      <td class="px-6 py-4 italic text-xs">IconPropType</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic text-balance font-bold text-primary-600">Iconos inyectados con padding automático.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default InputPage
